import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useDataContext } from 'src/contexts';
import { safeLocalStorage } from 'src/utils/local-storage';
import { notifyQRCodeScanned } from 'src/utils/pvo';
import { useFetchPvoData } from './use-fetch-pvo-data';

const PW_ID_STORAGE_KEY = 'pw_id';

function syncPlaywallIdFromStorage(
  pwIdFromStorage: string | null,
  currentPwId: string | null,
  setPlaywallId: (id: string | null) => void
) {
  if (pwIdFromStorage && currentPwId !== pwIdFromStorage) {
    setPlaywallId(pwIdFromStorage);
  }
}

export const useInitialize = () => {
  const { hash } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchPvoData, isLoading } = useFetchPvoData();
  const initializedHashRef = useRef<string | null>(null);

  const currentPwId = useDataContext((ctx) => ctx.playwallId);
  const setPlaywallId = useDataContext((ctx) => ctx.setPlaywallId);

  useEffect(() => {
    const pwIdFromUrl = searchParams.get('pw_id');
    const pwIdFromStorage = safeLocalStorage.getItem(PW_ID_STORAGE_KEY);

    const alreadyInitializedForThisHash =
      hash && initializedHashRef.current === hash;
    if (alreadyInitializedForThisHash) return;

    if (hash) initializedHashRef.current = hash;

    if (pwIdFromUrl) {
      if (currentPwId !== pwIdFromUrl) setPlaywallId(pwIdFromUrl);
      safeLocalStorage.setItem(PW_ID_STORAGE_KEY, pwIdFromUrl);
      if (hash) void notifyQRCodeScanned(hash);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('pw_id');
      setSearchParams(nextParams, { replace: true });
      return;
    }

    syncPlaywallIdFromStorage(pwIdFromStorage, currentPwId, setPlaywallId);

    if (!hash) return;

    void fetchPvoData({ redirectOnError: false });
  }, [
    searchParams,
    setSearchParams,
    setPlaywallId,
    currentPwId,
    hash,
    fetchPvoData,
  ]);

  return { isLoading };
};
